import { BufferAttribute, BufferGeometry, CircleGeometry, ConeGeometry, CylinderGeometry, Float32BufferAttribute, IcosahedronGeometry, OctahedronGeometry } from "three";

/**
 * Arestas internas que o desenho esconde (a componente da baricêntrica vira 1 nos três cantos, e a aresta
 * oposta a ela some): `fan`, os raios do centro do `CircleGeometry` (o centro é o 3º canto); `quads`, as
 * diagonais das faces laterais do `CylinderGeometry` aberto (triângulos (a, b, d) e (b, c, d)).
 */
export type HiddenEdges = "nenhuma" | "fan" | "quads";

/** Geometria sem índice com a coordenada baricêntrica de cada canto (`aBary`): as arestas no shader (F78). */
export function withBary(geometry: BufferGeometry, hidden: HiddenEdges = "nenhuma"): BufferGeometry {
  const g = geometry.index ? geometry.toNonIndexed() : geometry;
  const n = g.getAttribute("position").count;
  const bary = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    bary[i * 3 + (i % 3)] = 1;
    const tri = Math.floor(i / 3);
    if (hidden === "fan") bary[i * 3] = bary[i * 3 + 1] = 1;
    if (hidden === "quads") bary[i * 3 + (tri % 2 === 0 ? 0 : 1)] = 1;
  }
  g.setAttribute("aBary", new BufferAttribute(bary, 3));
  g.deleteAttribute("normal");
  g.deleteAttribute("uv");
  return g;
}

/**
 * Os atributos "por instância" do programa das peças (`skill-mesh.ts`) como atributos de verdade, por
 * vértice e na identidade (posição 0, giro nenhum, escala 1, cor somada 0 e opacidade 1, parâmetros 0),
 * para a malha solta (orbe, vitrines) que usa a forma. Sem eles, o valor viria do atributo genérico do
 * WebGL, que é estado global do contexto e não do VAO: outro programa que escreve um padrão na mesma
 * posição o deixaria errado (revisão do Gate B). O lote (`SkillBatch`) troca estes pelos instanciados.
 */
export function withInstanceIdentity(geometry: BufferGeometry): BufferGeometry {
  const n = geometry.getAttribute("position").count;
  const fill = (size: number, value: readonly number[]) => {
    const out = new Float32Array(n * size);
    for (let i = 0; i < n; i++) out.set(value, i * size);
    return new BufferAttribute(out, size);
  };
  geometry.setAttribute("iOffset", fill(3, [0, 0, 0]));
  geometry.setAttribute("iRot", fill(4, [0, 0, 0, 1]));
  geometry.setAttribute("iScale", fill(4, [0, 0, 0, 1]));
  geometry.setAttribute("iColor", fill(4, [0, 0, 0, 1]));
  geometry.setAttribute("iParams", fill(4, [0, 0, 0, 0]));
  return geometry;
}

/** Pena geométrica: um losango alongado (dois triângulos) em pé no plano XY, de 0 a 1 de altura. */
function feather(): BufferGeometry {
  const g = new BufferGeometry();
  g.setAttribute("position", new Float32BufferAttribute([0, 0, 0, 0.12, 0.45, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, -0.12, 0.45, 0], 3));
  return g;
}

/**
 * As peças das habilidades (doc 09 §3.4), feitas uma vez e divididas: o domo do Bastião (icosaedro), a lança
 * triangular do Salto, o prisma do Rompe-Núcleo (deitado em Z), a pena da Fênix, a coluna de luz do reviver,
 * o nó da rede (guia §18), o triângulo da Runa e a faceta hexagonal (deitados no chão).
 */
const shape = (geometry: BufferGeometry, hidden?: HiddenEdges) => withInstanceIdentity(withBary(geometry, hidden));

export const SKILL_SHAPES = {
  domo: shape(new IcosahedronGeometry(1, 1)),
  lanca: shape(new ConeGeometry(0.1, 1.3, 3, 1, true).translate(0, 0.65, 0)),
  prisma: shape(new CylinderGeometry(0.3, 0.3, 1, 3, 1, true).rotateX(Math.PI / 2), "quads"),
  pena: shape(feather()),
  coluna: shape(new CylinderGeometry(0.55, 0.55, 1, 6, 1, true).translate(0, 0.5, 0), "quads"),
  no: shape(new OctahedronGeometry(0.16)),
  triangulo: shape(new CircleGeometry(0.45, 3).rotateX(-Math.PI / 2), "fan"),
  faceta: shape(new CircleGeometry(0.4, 6).rotateX(-Math.PI / 2), "fan"),
} as const;

export type SkillShapeId = keyof typeof SKILL_SHAPES;
